import React, { useState, useEffect } from 'react';
import {
  Box,
  Sheet,
  Typography,
  Avatar,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Badge,
  IconButton,
} from '@mui/joy';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';

const MainLayout = ({ children, currentPage }) => {
  const { user, logout, isAdmin } = useAuth();
  const { showSuccess } = useNotification();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [actualCurrentPage, setActualCurrentPage] = useState('dashboard');

  // Auto-detect current page from URL
  useEffect(() => {
    const detectCurrentPage = () => {
      const path = window.location.pathname;
      const pageId = path.split('/')[1] || 'dashboard';

      // Map URL paths to page IDs
      const pathToPageId = {
        'dashboard': 'dashboard',
        'devices': 'devices',
        'device-groups': 'device-groups',
        'content': 'content',
        'layouts': 'layouts',
        'scheduling': 'scheduling',
        'analytics': 'analytics',
        'users': 'users',
        'settings': 'settings'
      };

      const detectedPage = pathToPageId[pageId] || 'dashboard';
      console.log('Detected page:', detectedPage, 'from path:', path);
      setActualCurrentPage(detectedPage);
    };

    // Run immediately
    detectCurrentPage();

    // Listen for URL changes
    const handlePopState = () => detectCurrentPage();
    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleLogout = () => {
    logout();
    showSuccess('Successfully logged out');
  };

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: '📊',
      path: '/dashboard',
    },
    {
      id: 'devices',
      label: 'Devices',
      icon: '📺',
      path: '/devices',
    },
    {
      id: 'device-groups',
      label: 'Device Groups',
      icon: '📱',
      path: '/device-groups',
    },
    {
      id: 'content',
      label: 'Content Management',
      icon: '🎬',
      path: '/content',
    },
    {
      id: 'layouts',
      label: 'Layouts',
      icon: '🎬',
      path: '/layouts',
    },
    {
      id: 'scheduling',
      label: 'Scheduling',
      icon: '📅',
      path: '/scheduling',
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: '📈',
      path: '/analytics',
    },
    ...(isAdmin() ? [{
      id: 'users',
      label: 'User Management',
      icon: '👥',
      path: '/users',
    }] : []),
    {
      id: 'settings',
      label: 'Settings',
      icon: '⚙️',
      path: '/settings',
    },
  ];

  const handleNavigation = (path) => {
    window.location.href = path;
    setMobileMenuOpen(false);

    // Update current page immediately
    const pageId = path.split('/')[1] || 'dashboard';
    const pathToPageId = {
      'dashboard': 'dashboard',
      'devices': 'devices',
      'device-groups': 'device-groups',
      'content': 'content',
      'layouts': 'layouts',
      'scheduling': 'scheduling',
      'analytics': 'analytics',
      'users': 'users',
      'settings': 'settings'
    };
    const detectedPage = pathToPageId[pageId] || 'dashboard';
    setActualCurrentPage(detectedPage);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sheet
        sx={{
          width: 280,
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          borderRight: '1px solid',
          borderColor: 'neutral.200',
          background: 'linear-gradient(180deg, #fff 0%, #f8f9fa 100%)',
          zIndex: 1000,
          display: { xs: mobileMenuOpen ? 'block' : 'none', md: 'block' },
        }}
      >
        {/* Logo */}
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'neutral.200' }}>
          <img src="/logo-vector.svg" alt="Vector India" style={{ width: '200px' }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <Typography level="title-md" sx={{ color: 'primary.500', textAlign: 'center', fontWeight: 600 }}>
                Digital Signage
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Navigation */}
        <Box sx={{ p: 2, flex: 1 }}>
          <List>
            {navigationItems.map((item) => {
              const isSelected = actualCurrentPage === item.id;
              console.log(`Item ${item.id}: selected=${isSelected}, actualCurrentPage=${actualCurrentPage}`);
              return (
                <ListItem key={item.id} sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: '8px',
                      '&.Mui-selected': {
                        background: 'linear-gradient(135deg, rgba(235, 122, 36, 0.1) 0%, rgba(241, 155, 48, 0.1) 100%)',
                        color: 'primary.500',
                        fontWeight: 600,
                      },
                    }}
                  >
                    <ListItemDecorator sx={{ fontSize: '18px' }}>
                      {item.icon}
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-md">{item.label}</Typography>
                    </ListItemContent>
                    {item.id === 'devices' && (
                      <Badge size="sm" color="success" variant="solid">12</Badge>
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* System Status */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'neutral.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'success.500' }} />
            <Typography level="body-sm" sx={{ color: 'success.600' }}>
              All systems operational
            </Typography>
          </Box>
          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
            Last updated: {new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      </Sheet>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, ml: { md: '280px' } }}>
        {/* Header */}
        <Sheet
          sx={{
            height: 70,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            borderBottom: '1px solid',
            borderColor: 'neutral.200',
            background: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 999,
          }}
        >
          {/* Mobile menu button */}
          <IconButton
            variant="outlined"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            sx={{ display: { md: 'none' } }}
          >
            ☰
          </IconButton>

          <Typography level="h4" sx={{ color: 'neutral.800', fontWeight: 600 }}>
            {navigationItems.find(item => item.id === currentPage)?.label || 'Dashboard'}
          </Typography>

          {/* User Menu */}
          <Dropdown>
            <MenuButton
              variant="plain"
              size="sm"
              sx={{
                borderRadius: '50px',
                '&:hover': {
                  backgroundColor: 'neutral.100',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  size="sm"
                  sx={{
                    background: 'linear-gradient(135deg, #eb7a24 0%, #f19b30 100%)',
                  }}
                >
                  {user?.name?.charAt(0) || 'U'}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                    {user?.name}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                    {user?.role}
                  </Typography>
                </Box>
              </Box>
            </MenuButton>
            <Menu sx={{ minWidth: 200 }}>
              <MenuItem onClick={() => handleNavigation('/profile')}>
                <ListItemDecorator>👤</ListItemDecorator>
                Profile Settings
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/change-password')}>
                <ListItemDecorator>🔐</ListItemDecorator>
                Change Password
              </MenuItem>
              {isAdmin() && (
                <MenuItem onClick={() => handleNavigation('/users')}>
                  <ListItemDecorator>👥</ListItemDecorator>
                  User Management
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: 'danger.500' }}>
                <ListItemDecorator>🚪</ListItemDecorator>
                Logout
              </MenuItem>
            </Menu>
          </Dropdown>
        </Sheet>

        {/* Content */}
        <Box sx={{ p: 3, minHeight: 'calc(100vh - 70px)' }}>
          {children}
        </Box>
      </Box>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            display: { md: 'none' },
          }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </Box>
  );
};

export default MainLayout;